// Zebra Offline Reports — frontend API surface.
//
// Gone with the placeholder syntax: uploadTemplate, convertTemplate and
// downloadTemplate. A report's source is its module in the registry seam, so
// there is no file to send up and none to fetch back. What is left is the
// admin's metadata, the renders, and the preview.

import { client, API_BASE } from '@platform/http/api-client';

// Pull { blob, filename } out of a binary response.
function asDownload(response, fallback) {
  const cd = response.headers['content-disposition'] || '';
  const m = cd.match(/filename="?([^"]+)"?/);
  return { blob: response.data, filename: m ? m[1] : fallback };
}

// An error from a blob-typed request arrives as a Blob, not JSON — read it so
// the screen can show the server's actual message instead of "Request failed".
export async function errorMessage(err, fallback = 'Something went wrong') {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try { return JSON.parse(await data.text()).error || fallback; } catch { /* not JSON */ }
  }
  return data?.error || err?.message || fallback;
}

const offlineReportsApi = {
  // Scope-filtered list. `surface` says where the caller is asking from, so a
  // report offered only on work items never shows up in the search export.
  async listReports({ issueTypeId = null, projectKey = null, surface = null, enabledOnly = false } = {}) {
    const params = {};
    if (issueTypeId) params.issueTypeId = issueTypeId;
    if (projectKey) params.projectKey = projectKey;
    if (surface) params.surface = surface;
    if (enabledOnly) params.enabled = '1';
    return (await client.get('/api/offline-reports/templates', { params })).data;
  },

  async getReport(id) {
    return (await client.get(`/api/offline-reports/templates/${id}`)).data;
  },

  // Mint a report by NAME — the Client Customs flow. A developer binds code
  // to the returned slug afterwards.
  async createReport({ name, description }) {
    return (await client.post('/api/offline-reports/templates', { name, description })).data;
  },

  // Everything an admin may change: name, description, the three scopes,
  // which formats are offered and which is the default.
  async updateReport(id, patch) {
    return (await client.put(`/api/offline-reports/templates/${id}`, patch)).data;
  },

  // Any report. One the code ships is RETIRED — it stays gone across deploys
  // and appears in listRetiredReports() with a way back.
  async deleteReport(id) {
    return (await client.delete(`/api/offline-reports/templates/${id}`)).data;
  },

  async listRetiredReports() {
    return (await client.get('/api/offline-reports/templates/retired')).data;
  },

  async restoreReport(slug) {
    return (await client.post(`/api/offline-reports/templates/retired/${encodeURIComponent(slug)}/restore`)).data;
  },

  async renderReport(id, { issueKey, outputFormat } = {}) {
    const response = await client.post(
      `/api/offline-reports/templates/${id}/render`,
      { issueKey, outputFormat },
      { responseType: 'blob' }
    );
    return asDownload(response, `report-${id}`);
  },

  // Batch render from a search result. The server applies the report's own
  // work-type and project scope before building.
  //
  // `source` is either { jql } — the user's SEARCH, which the server resolves
  // to the whole result set — or { issueKeys } for a hand-picked list. The
  // query form matters: sending keys meant sending only the rows the grid had
  // loaded, so an export of a 3,000-row search produced 200.
  async renderMany(id, source, outputFormat = null) {
    const body = Array.isArray(source) ? { issueKeys: source } : { ...source };
    const response = await client.post(
      `/api/offline-reports/templates/${id}/render-many`,
      { ...body, outputFormat },
      { responseType: 'blob' }
    );
    const intHeader = (h) => {
      const n = parseInt(response.headers[h], 10);
      return Number.isFinite(n) ? n : null;
    };
    return {
      ...asDownload(response, `batch-${id}`),
      stats: {
        requested: intHeader('x-batch-requested'),
        rendered: intHeader('x-batch-rendered'),
        matched: intHeader('x-batch-matched'),
        capped: response.headers['x-batch-capped'] === '1',
      },
    };
  },

  // Admin preview: always a PDF, for showing inline. Returns an object URL
  // the caller must revoke when it closes the viewer.
  async previewReport(id, { issueKey, issueKeys } = {}) {
    const response = await client.post(
      `/api/offline-reports/templates/${id}/preview`,
      { issueKey, issueKeys },
      { responseType: 'blob' }
    );
    const blob = new Blob([response.data], { type: 'application/pdf' });
    return { blob, url: URL.createObjectURL(blob) };
  },

  async listRuns({ templateId = null, issueKey = null, limit = 100 } = {}) {
    const params = { limit };
    if (templateId) params.templateId = templateId;
    if (issueKey) params.issueKey = issueKey;
    return (await client.get('/api/offline-reports/runs', { params })).data;
  },

  // Schedules
  async listSchedules({ enabledOnly = false } = {}) {
    const params = {};
    if (enabledOnly) params.enabled = '1';
    return (await client.get('/api/offline-reports/schedules', { params })).data;
  },
  async getSchedule(id) {
    return (await client.get(`/api/offline-reports/schedules/${id}`)).data;
  },
  async createSchedule(data) {
    return (await client.post('/api/offline-reports/schedules', data)).data;
  },
  async updateSchedule(id, patch) {
    return (await client.put(`/api/offline-reports/schedules/${id}`, patch)).data;
  },
  async deleteSchedule(id) {
    return (await client.delete(`/api/offline-reports/schedules/${id}`)).data;
  },
  async runScheduleNow(id) {
    return (await client.post(`/api/offline-reports/schedules/${id}/run-now`)).data;
  },

  // When the schedule being edited would actually fire. Answered by the
  // server so the preview and the scheduler are the same cron evaluation
  // in the same zone, rather than two implementations that agree until
  // the clocks change. → { runs: [ISO strings], timezone }
  async previewSchedule({ cron_expression, timezone, count = 3 }) {
    return (await client.post('/api/offline-reports/schedules/preview',
      { cron_expression, timezone, count })).data;
  },

  // How many work items a ZQL matches, and the row cap the render applies.
  // Answered by the same engine and the same cap the export will use, so
  // the number on screen is a promise about the file.
  // → { count, limit }
  async zqlCount(zql) {
    return (await client.post('/api/offline-reports/zql-count', { zql })).data;
  },

  // Module-wide settings — currently the default email message.
  async getReportSettings() {
    return (await client.get('/api/offline-reports/settings')).data;
  },
  async saveReportSettings(patch) {
    return (await client.put('/api/offline-reports/settings', patch)).data;
  },

  // Saved filters, for the data-source picker. The reports admin screen is
  // admin-only, so this returns every filter on the site.
  async listSavedFilters() {
    const data = (await client.get('/api/filters', { params: { maxResults: 500 } })).data;
    return Array.isArray(data?.filters) ? data.filters : (Array.isArray(data) ? data : []);
  },
};

// Trigger a browser download for a fetched blob.
export function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement('a'), { href: url, download: filename });
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export { API_BASE };
export default offlineReportsApi;
